import {useEffect, useMemo, useState} from 'react';
import { useTranslation } from 'react-i18next';

import { suggestionInputHoc, SuggestionInputHocProps } from '~src/hocs/suggestionInput';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { SelectedOption } from '~src/shared/selectors/DdSelectorTrigger';
import DdPopoverSelector from '~src/shared/selectors/DdPopoverSelector';

import { HoverSortingContainer, HoverSortingContentBox, SortContainer, SortingItem } from './sortSelector.sc';
import SvgIcon from '~src/shared/SvgIcon';
import { SvgIconName } from '~src/shared/SvgIcon';

export interface SortingOption<TYPE> {
  name: TYPE;
  translationKey: string;
  label?: string;
  icon?: SvgIconName;
}

interface OwnProps<TYPE> {
  selectedSorting?: TYPE;
  sortingOptions?: ReadonlyArray<Readonly<SortingOption<TYPE>>>;
  boxTopPosition?: number;
  selectorLabelKey?: string;
  onHeader?: boolean;
  selectorCssClass?: string;
  selectorWithLabel?: boolean;
  sortList?: (sort: TYPE) => void;
}

type Props<TYPE> = OwnProps<TYPE> & SuggestionInputHocProps;

export function SortSelector<TYPE>(props: Props<TYPE>) {
  const {
    selectedSorting = SortingValues.DefaultString_asc,
    selectorCssClass,
    selectorWithLabel,
    selectorLabelKey = 'x.reports.topics.sorting.label',
    boxTopPosition = 100,
    sortingOptions = [],
  } = props;
  const { t } = useTranslation();
  const [showToggleBox, setShowToggleBox] = useState(false);
  useEffect(() => {
    props.checkHeight();
    if (showToggleBox) {
      props.addEventListener();
    }

    return () => {
      props.removeEventListener();
    };
  }, [showToggleBox]);

  const [currOpt, setCurrOpt] = useState<SelectedOption>();
  useEffect(() => {
    const currOptItem = sortingOptions.find(opt => opt.name === selectedSorting);
    if (currOptItem) {
      setCurrOpt({ nameKey: currOptItem.translationKey });
    }
  }, [selectedSorting]);

  const toggleBox = (visible: boolean) => {
    setShowToggleBox(visible);
    props.checkHeight();
  };

  const onSortItemClick = (option: SortingOption<TYPE>) => {
    props.sortList && props.sortList(option.name);
    toggleBox(false);
  };

  const popoverContent = useMemo(() => {

    return (
      <HoverSortingContentBox
        style={{
          maxHeight: `calc(100vh - ${boxTopPosition}px)`,
        }}
      >
        <SortContainer>
          {sortingOptions.map(option => (
            <SortingItem
              key={option.translationKey + option.icon || ''}
              onClick={onSortItemClick.bind(null, option)}
              $selected={selectedSorting === option.name}
              data-translation={option.translationKey}
            >
              <span style={{ display: 'flex' }}>
                {option.label || t(option.translationKey) || ''}{' '}
                {option.icon ? (
                  <span style={{ marginLeft: '0.5rem' }}>
                    <SvgIcon name={option.icon} />
                  </span>
                ) : null}
              </span>
            </SortingItem>
          ))}
        </SortContainer>
      </HoverSortingContentBox>
    );
  }, [sortingOptions, selectedSorting, boxTopPosition, props.sortList]);

  return (
    <HoverSortingContainer>
      <DdPopoverSelector
        triggerLabelKey={selectorWithLabel ? selectorLabelKey : ''}
        triggerCssClass={selectorCssClass}
        selectedOption={currOpt}
        popoverContent={popoverContent}
        isPopoverVisible={showToggleBox}
        togglePopover={toggleBox}
      />
    </HoverSortingContainer>
  );
}


const SortSelectorSH: <T>(props: OwnProps<T>) => JSX.Element = suggestionInputHoc(SortSelector);
export default SortSelectorSH;
